import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { PremiumSponsorship } from "../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../services/premiumSponsorship/premiumSponsorshipService";
import { useConfirmationWithIntl } from "../../components/ConfirmationDialog";
import CandidateIcon from "../candidate/CandidateIcon";

const {
  acceptPremiumSponsorship,
  declinePremiumSponsorship,
  renouncePremiumSponsorship,
} = premiumSponsorshipService();

// Pastille de l'organisation sponsor : deux premières initiales, calculées sur le nom renvoyé par l'API.
const initials = (name?: string) =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const PremiumSponsorshipSection = ({
  sponsorships,
  onChange,
}: {
  sponsorships: PremiumSponsorship[];
  onChange: () => void;
}) => {
  const { t } = useTranslation(["accounts"]);
  const { confirm } = useConfirmationWithIntl(["accounts"]);

  const pending = sponsorships.filter((s) => s.status === "invited");
  const active = sponsorships.filter(
    (s) => s.status === "accepted" && s.premiumGranted === true,
  );

  if (pending.length === 0 && active.length === 0) return null;

  return (
    <section className="ui-card --pad cand-card">
      <div className="cand-card_head">
        <h2 className="ui-st">
          {t("accounts:premium-sponsorship.SECTION_TITLE")}
        </h2>
        {active.length > 0 && (
          <span className="ui-chip --prem">
            <CandidateIcon name="bolt" />
            {t("accounts:premium-sponsorship.active.BADGE")}
          </span>
        )}
      </div>
      <div className="cand-card_body cand-sponsorships">
        {pending.map((sponsorship) => (
          <div key={`pending-${sponsorship.id}`} className="ui-banner">
            <div className="ui-banner_grow">
              <p>
                {t("accounts:premium-sponsorship.pending.DESCRIPTION", {
                  replace: {
                    organization: sponsorship.organization?.legalName,
                  },
                })}
              </p>
              <div className="cand-sponsorship_actions">
                <button
                  className="btn--2"
                  onClick={() => {
                    void declinePremiumSponsorship(sponsorship.token!).then(
                      onChange,
                    );
                  }}
                >
                  {t("accounts:premium-sponsorship.pending.DECLINE_BUTTON")}
                </button>
                <button
                  className="btn--1"
                  onClick={() => {
                    void acceptPremiumSponsorship(sponsorship.token!).then(
                      () => {
                        window.location.reload();
                      },
                      (err) => {
                        // Session died before the click: `api.ts` has already
                        // logged us out, so reloading hands the candidate over
                        // to the private-route guard, i.e. the login page.
                        if (err?.response?.status === 401)
                          window.location.reload();
                      },
                    );
                  }}
                >
                  {t("accounts:premium-sponsorship.pending.ACCEPT_BUTTON")}
                </button>
              </div>
            </div>
          </div>
        ))}
        {active.map((sponsorship) => (
          <div key={`active-${sponsorship.id}`} className="cand-sponsorship">
            <span className="cand-avatar" aria-hidden="true">
              {initials(sponsorship.organization?.legalName)}
            </span>
            <div className="cand-sponsorship_txt">
              <b>
                {sponsorship.respondedAt
                  ? t(
                      "accounts:premium-sponsorship.active.SPONSORED_BY_SINCE",
                      {
                        replace: {
                          organization: sponsorship.organization?.legalName,
                          date: format(
                            new Date(sponsorship.respondedAt),
                            "dd/MM/yyyy",
                          ),
                        },
                      },
                    )
                  : t("accounts:premium-sponsorship.active.SPONSORED_BY", {
                      replace: {
                        organization: sponsorship.organization?.legalName,
                      },
                    })}
              </b>
              <p className="ui-muted ui-small">
                {t("accounts:premium-sponsorship.active.BENEFITS")}
              </p>
              <div className="cand-sponsorship_actions">
                <button
                  className="btn--error"
                  onClick={() => {
                    confirm(
                      "accounts:premium-sponsorship.active.confirm-renounce.MESSAGE",
                      () =>
                        renouncePremiumSponsorship(sponsorship.id).then(
                          onChange,
                        ),
                      true,
                    );
                  }}
                >
                  {t("accounts:premium-sponsorship.active.RENOUNCE_BUTTON")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PremiumSponsorshipSection;
